import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import {
  SelectorPiece,
  serializeSelector,
  getSelectorPiecesForTrimmedSelector,
  toSelectorPieces,
} from '../utils/selector-tree';

export const useSelectorTree = (
  selector: string,
  canonicalizedSelector: string | undefined | null,
  onChangeSelector: (selector: string) => void,
  onHoverSelector: (selector?: string) => void,
  enabled: boolean = true,
) => {
  // null is invalid, empty array means no selector
  const [selectorParts, _setSelectorParts] = useState<SelectorPiece[][] | null>([]);
  // wrap state setter to call on change when the selector changes.
  // function API matches useState.
  const setSelectorParts = useCallback(
    (
      value:
        | SelectorPiece[][]
        | null
        | ((value: SelectorPiece[][] | null) => SelectorPiece[][] | null),
    ) => {
      _setSelectorParts(prevSelectorParts => {
        const newSelectorParts = typeof value === 'function' ? value(prevSelectorParts) : value;
        if (newSelectorParts && enabled) onChangeSelector(serializeSelector(newSelectorParts));
        return newSelectorParts;
      });
    },
    [_setSelectorParts, onChangeSelector, enabled],
  );

  const [trimming, setTrimming] = useState(false);
  const notifications = useNotifications();
  const [trimmedSelectorParts, setTrimmedSelectorParts] = useState<SelectorPiece[][]>([]);
  const flags = useFlags();
  const showOptimizer = flags.featureFlags().AppObjectPickerPrototype;

  const [showHidden, setShowHidden] = useState<boolean>(false);

  const serializedSelector = useMemo(() => serializeSelector(selectorParts), [selectorParts]);

  const onTrimStart = useCallback(() => {
    if (!selectorParts) return;

    setTrimmedSelectorParts([...selectorParts]);
    setTrimming(true);
  }, [selectorParts]);

  const onTrimFinish = useCallback(
    (selector: string, error: string | undefined) => {
      if (!selectorParts) return;

      if (error) {
        notifications.error(error);
      }
      const newParts = getSelectorPiecesForTrimmedSelector(selectorParts, selector);

      setTrimming(false);
      setSelectorParts(newParts);
    },
    [selectorParts, setSelectorParts],
  );

  const onTrimPartial = useCallback(
    (s: string) => {
      if (!selectorParts) return;

      const newParts = getSelectorPiecesForTrimmedSelector(selectorParts, s);
      setTrimmedSelectorParts(newParts);
    },
    [selectorParts],
  );

  useEffect(() => {
    const run = async () => {
      try {
        setSelectorParts(toSelectorPieces(selector, canonicalizedSelector || undefined));
      } catch (e) {
        setSelectorParts(null);
      }
    };
    run();
  }, [selector, canonicalizedSelector]);

  const onHoverSelectorPart = useCallback(
    (partIndex: number | undefined) => {
      if (typeof partIndex === 'undefined') {
        onHoverSelector(undefined);
        return;
      }

      if (!selectorParts) {
        onHoverSelector(undefined);
        return;
      }

      const hoverBoundary = partIndex + 1;

      // The prefix is the part of the selector that we actually want to
      // highlight.
      const prefix = selectorParts.slice(0, hoverBoundary);
      const prefixSelector = serializeSelector(prefix);

      // We use :has(suffix) to ensure that we only highlight ancestors of
      // elements that match the full selector.
      const suffix = selectorParts.slice(hoverBoundary);
      const suffixSelector = serializeSelector(suffix);
      const selector =
        suffixSelector.length > 0 ? `${prefixSelector}:has(${suffixSelector})` : prefixSelector;

      onHoverSelector(selector);
    },
    [onHoverSelector, selectorParts],
  );

  // if disabled, return no-op functions
  if (!enabled) {
    return {
      serializedSelector: selector,
      selector,
      onChangeSelector,
      // unused for non-web platforms
      selectorParts: [],
      setSelectorParts: () => {},
      trimmedSelectorParts: [],
      setTrimmedSelectorParts: () => {},
      trimming: false,
      setTrimmi: () => {},
      showOptimizer: false,
      onTrimStart: () => {},
      onTrimFinish: () => {},
      onTrimPartial: () => {},
      showHidden: false,
      setShowHidden: () => {},
      onHoverSelectorPart: () => {},
    };
  }

  return {
    selectorParts,
    setSelectorParts,
    trimmedSelectorParts,
    setTrimmedSelectorParts,
    trimming,
    setTrimming,
    showOptimizer,
    onTrimStart,
    onTrimFinish,
    onTrimPartial,
    showHidden,
    setShowHidden,
    serializedSelector,
    selector,
    onChangeSelector,
    onHoverSelectorPart,
  };
};

export type SelectorTreeHookState = ReturnType<typeof useSelectorTree>;
